<template>
    <pendo-multiselect-filter
        ref="multiselect"
        :show-apply="showApply"
        :value="selectedOptions"
        :entity-name="$t('multiselectGroupsFilter.productAreas')"
        :all-label="$t('multiselectGroupsFilter.allProductAreas')"
        show-search
        :menu-width="350"
        :use-data-measure-trigger="useDataMeasureTrigger"
        :loading="fetching || loading"
        :options="multiselectOptions"
        :show-action="canEdit"
        :show-all-label="showAllLabel"
        :disabled="disabled"
        @open="loadOptions"
        @input="updateSelectedOptions"
        @leave-lower-bound="onLeaveLowerBound">
        <template
            v-if="canCreate"
            #footer="{ handleKeydown }">
            <pendo-button
                ref="createGroupButton"
                theme="app"
                type="subtle"
                data-cy="create-group"
                prefix-icon="plus"
                :label="$t('multiselectGroupsFilter.createProductArea')"
                @click="createGroup"
                @keydown="handleKeydown($event, footerKeydownOverrides(createGroup))" />
        </template>
        <template #option="{ option, index }">
            <pendo-option-insert
                v-pendo-tooltip="{
                    content: tooltipContent,
                    multiline: true,
                    arrow: true,
                }"
                :index="index"
                :option="option"
                :actions="optionActions(option)"
                :show-checkbox="true"
                @edit="editGroup"
                @mouseover="onMouseover($event, option)" />
        </template>
    </pendo-multiselect-filter>
</template>

<script>
import { getGroupColorByCSSClass } from '@/groups/group-color-picker/group-color-service';
import { elementHasOverflow, findNodeByContent } from '@/utils/dom.utils';
import {
    PendoButton,
    PendoOptionInsert,
    PendoMultiselectFilter,
    PendoTooltip,
    PendoMultiselectHelpers,
} from '@pendo/components';
import { onLeaveLowerBound } from '@/utils/multiselect-keyboard';
import { mapActions, mapGetters, mapState } from 'vuex';
const { actionButtonKeyOverride, verticalNavigationKeyOverride } = PendoMultiselectHelpers;

export default {
    name: 'MultiselectGroupsFilter',
    components: {
        PendoMultiselectFilter,
        PendoButton,
        PendoOptionInsert,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        value: {
            type: Array,
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        hideCreate: {
            type: Boolean,
            default: false,
        },
        hideEdit: {
            type: Boolean,
            default: false,
        },
        persist: {
            type: Boolean,
            default: true,
        },
        useDataMeasureTrigger: {
            type: Boolean,
            default: true,
        },
        showApply: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['input', 'createGroup', 'editGroup'],
    data () {
        return {
            loadTriggered: false,
            options: [],
            tooltipContent: null,
        };
    },
    computed: {
        ...mapState('groups', {
            fetching: (state) => state.fetching,
            savedFilter: (state) => state.idFilter,
        }),
        ...mapGetters('groups', {
            groupsList: 'list',
        }),
        selectedOptions () {
            // when persisting, reference the global product areas filter, otherwise use the locally provided values
            const selectedGroupIds = this.persist ? this.savedFilter || [] : this.value;

            return this.multiselectOptions.filter((group) => selectedGroupIds.includes(group.id));
        },
        multiselectOptions () {
            // id is set as an empty string since that is the value of group.id for items w/o a group
            const options = [{ name: this.$t('multiselectGroupsFilter.noProductArea'), id: '' }, ...this.groupsList].map((group) => {
                const groupOption = {
                    id: group.id,
                    label: group.name,
                    icon: { fill: this.getColor(group.color), size: 18, strokeWidth: 0, type: 'square' },
                };
                if (this.canEdit && group.id) {
                    groupOption.actions = [
                        {
                            type: 'edit',
                            tooltip: this.$t('multiselectGroupsFilter.editProductArea'),
                            icon: {
                                type: 'edit',
                            },
                        },
                    ];
                }

                return groupOption;
            });

            return options;
        },
        canCreate () {
            return !this.hideCreate && this.$can({ action: 'create', entity: 'groups' });
        },
        canEdit () {
            return !this.hideEdit && this.$can({ action: 'edit', entity: 'groups' });
        },
        showAllLabel () {
            return !this.value.length || !this.options.length || (this.loadTriggered && !this.fetching);
        },
        optionActions () {
            return (option) => option.actions || [];
        },
    },
    created () {
        this.loadOptions();
    },
    methods: {
        ...mapActions('groups', ['loadAll', 'updateFilter']),
        createGroup () {
            this.$emit('createGroup');
        },
        editGroup (group) {
            this.$emit('editGroup', group);
        },
        getColor (className) {
            if (className && className[0] !== '.') {
                className = `.${className}`;
            }

            return getGroupColorByCSSClass(className);
        },
        async loadOptions () {
            if (this.loadTriggered) return;
            this.loadTriggered = true;
            await this.loadAll({});

            if (this.persist) {
                this.$emit('input', this.savedFilter);
            }
        },
        updateSelectedOptions (options) {
            const ids = options.map((group) => group.id);
            this.$emit('input', ids);
            this.options = options;

            if (!this.persist) return;

            this.updateFilter({ idFilter: ids });
        },
        onLeaveLowerBound () {
            onLeaveLowerBound(this.$refs.multiselect, null, this.$refs.createGroupButton);
        },
        onMouseover ({ currentTarget }, option) {
            const contentNodes = findNodeByContent(option.label, currentTarget);
            if (!contentNodes.length) {
                return;
            }

            this.tooltipContent = elementHasOverflow(contentNodes[0]) ? option.label : null;
        },
        footerKeydownOverrides (actionFn) {
            return {
                ...actionButtonKeyOverride(actionFn),
                ...verticalNavigationKeyOverride(this.$refs.multiselect.focusInput),
            };
        },
        /**
         * @public
         */
        toggleMenu () {
            this.$refs.multiselect.toggleMenu();
        },
    },
};
</script>

<style lang="less" scoped>
:deep(.pendo-multiselect__option) {
    padding: 0 8px 0 16px;
}

:deep(.pendo-checkbox__label) {
    padding-right: 0 !important;
}

:deep(.pendo-multiselect-option__label-container) {
    height: 100%;
    display: flex;
    align-items: center;
}

:deep(.pendo-multiselect-option__label) {
    display: inline-block;
    width: 100%;
    height: 100%;
    line-height: 32px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>
